/* eslint-disable import/prefer-default-export */
/* eslint-disable max-lines */
import { isNull, reduce } from 'lodash';

import { transformPatientAddress } from 'api/patient/transform';
import titleCase from 'common/utils/titleCase';
import graphqlClient from '../graphql';
import {
  applyUrgentTagGql,
  associateOrderShipmentGql,
  cancelBulkLineItemsGql,
  cancelLineItemGql,
  cancelOrderGql,
  cancelOrderShipmentGql,
  cancelPrxShipmentGql,
  changePrescriptionReviewReasonGql,
  clearShipByDateOverrideGql,
  createOrderShipmentGql,
  createPOMOTCOrderGql,
  createPomOrderGql,
  createReshipmentGql,
  createShipmentForLineItemsGql,
  deleteIrreconciledOrderGql,
  deletePrxShipmentGql,
  fulfillProductByRxNumberGql,
  generateLabelForShipmentGql,
  getBatchShipmentsGql,
  getOrderSuggestionsForShipstationOrdersGql,
  getOrdersGql,
  getRecipientAddressesGql,
  getRxProofUploadUrlsGql,
  getShipmentCountsBatchGql,
  getShipmentCountsGql,
  getShipmentEventHistoryGql,
  getShipmentLabelGql,
  getShipstationShipmentCountsBatchGql,
  getShipstationShipmentsGql,
  listBatchShipmentsGql,
  listOrdersForShipmentGql,
  listOrdersGql,
  listProcessedExternalOrdersGql,
  listShipmentLineItemsGql,
  listShipmentsFromPrxGql,
  listShippingOptionsGql,
  listUnReconcileOrdersGql,
  manageProductFulfillmentGql,
  moveOrderToFillLaterGql,
  orderNeedsReviewGql,
  overrideFulfillmentNdcGql,
  prescriptionNeedsReviewGql,
  removeUrgentTagGql,
  reverifyOrderGql,
  sendOrderBackToQueueGql,
  setLineItemShipByDateGql,
  shipOrderLaterGql,
  splitLineItemQuantityGql,
  transferOrderDetailsGql,
  updateAddressOrderLineItemStatusGql,
  updateOrderAddressGql,
  updateOrderAssociationGql,
  updateShipmentGql,
  validateShipmentAddressGql,
  verifyOrderGql,
  voidShipmentGql,
} from './gql';
import {
  buildPrxSortBy,
  transformBatchShipments,
  transformListBatchShipments,
  transformListOrders,
  transformListOrdersForShipment,
  transformListPomOrders,
  transformOrderShipment,
  transformProcessedExternalOrders,
  transformPrxGenerateLabelResponse,
  transformPrxShipmentListResponse,
  transformPrxUpdateShipmentResponse,
  transformReconcileOrderSuggestions,
  transformShipmentLineItems,
  transformUnReconcileShipments,
} from './transform';

export const listOrdersApi = ({ page, sortBy, filter }) => {
  return graphqlClient
    .request(listOrdersGql, {
      offset: (page - 1) * 25,
      sortBy,
      filter: reduce(
        filter,
        (result, value, key) => ({
          ...result,
          [key]: isNull(value) ? undefined : value,
        }),
        {}
      ),
    })
    .then(transformListOrders);
};

export const fulfillProductByRxNumberApi = ({ rxNumber, drugDispensed }) => {
  return graphqlClient
    .request(fulfillProductByRxNumberGql, { rxNumber, drugDispensed })
    .then(({ fulfillProductByRxNumber }) => fulfillProductByRxNumber);
};

export const overrideFulfillmentNdcApi = ({ rxNumber, drugDispensed }) => {
  return graphqlClient
    .request(overrideFulfillmentNdcGql, { rxNumber, drugDispensed })
    .then(({ overrideFulfillmentNdc }) => overrideFulfillmentNdc);
};

export const manageProductFulfillmentApi = ({
  lineItemId,
  mpi,
  orderId,
  state,
  failureReason,
  lotAllocations,
  unassignedFill,
  markScanned,
}) => {
  return graphqlClient
    .request(manageProductFulfillmentGql, {
      lineItemId,
      mpi,
      orderId,
      state,
      failureReason,
      lotAllocations,
      unassignedFill,
      markScanned,
    })
    .then(({ manageProductFulfillmentStatus }) => manageProductFulfillmentStatus);
};

export const verifyOrderApi = ({
  orderId,
  mpi,
  isAutoShipEnabled,
  lineItemIds,
  shipmentType,
  skipAddressValidation,
  carrierCode,
  trackingNumber,
  externalOrderId,
  shipmentMode,
  shipDate,
  shipTime,
}) => {
  return graphqlClient
    .request(verifyOrderGql, {
      orderId,
      mpi,
      isAutoShipEnabled,
      lineItemIds,
      shipmentType,
      skipAddressValidation,
      carrierCode,
      trackingNumber,
      externalOrderId,
      shipmentMode,
      shipDate,
      shipTime,
    })
    .then(({ verifyOrder }) => verifyOrder);
};

export const createOrderShipmentApi = ({
  mpi,
  lineItemIds,
  shipmentType,
  shipmentMode,
  carrierCode,
  trackingNumber,
  externalShipmentId,
  shipDate,
  shipTime,
  isShipmentFromPrx,
  addressDirectoryId,
  isBatchShipment,
  nameOnAddress,
}) => {
  return graphqlClient
    .request(createOrderShipmentGql, {
      lineItemIds,
      mpi,
      shipmentType,
      shipmentMode,
      carrierCode,
      trackingNumber,
      externalShipmentId,
      shipDate,
      shipTime,
      isShipmentFromPrx,
      addressDirectoryId,
      isBatchShipment,
      nameOnAddress,
    })
    .then(transformOrderShipment);
};

export const cancelOrderApi = ({ orderId, cancelReason, cancelNote }) => {
  return graphqlClient
    .request(cancelOrderGql, { orderId, cancelReason, cancelNote })
    .then(({ cancelOrder }) => cancelOrder);
};

// The mutation takes an array of line items so a bulk screen can reuse it; the patient
// profile dialog splits one, so it sends a single entry.
// POM-6199: items is a list — one entry per line item being split, each with its own
// splits. A single-item split is just a list of one.
export const splitLineItemQuantityApi = ({ items }) => {
  return graphqlClient
    .request(splitLineItemQuantityGql, { input: { items } })
    .then(({ splitLineItemQuantity }) => splitLineItemQuantity);
};

export const moveOrderToFillLaterApi = ({
  orderId,
  scheduledFor,
  addressDirectoryId,
  isManuallyVerified,
}) => {
  return graphqlClient
    .request(moveOrderToFillLaterGql, {
      input: {
        orderId,
        scheduledFor,
        ...(addressDirectoryId ? { addressDirectoryId } : {}),
        ...(isManuallyVerified ? { isManuallyVerified: true } : {}),
      },
    })
    .then(({ moveOrderToFillLater }) => moveOrderToFillLater);
};

export const cancelLineItemApi = ({ orderId, lineItemId, cancelReason, cancelNote }) => {
  return graphqlClient
    .request(cancelLineItemGql, { orderId, lineItemId, cancelReason, cancelNote })
    .then(({ cancelLineItem }) => cancelLineItem);
};

export const cancelBulkLineItemsApi = ({ lineItemIds, cancelReason, cancelNote }) => {
  return graphqlClient
    .request(cancelBulkLineItemsGql, { lineItemIds, cancelReason, cancelNote })
    .then(({ cancelBulkLineItems }) => cancelBulkLineItems);
};

export const listOrdersForShipmentApi = ({ page, sortBy, filter }) => {
  return graphqlClient
    .request(listOrdersForShipmentGql, {
      offset: (page - 1) * 25,
      sortBy,
      filter: reduce(
        filter,
        (result, value, key) => ({
          ...result,
          [key]: isNull(value) ? undefined : value,
        }),
        {}
      ),
    })
    .then(transformListOrdersForShipment);
};

export const orderNeedsReviewApi = ({ orderId, mpi }) =>
  graphqlClient
    .request(orderNeedsReviewGql, { orderId, mpi })
    .then(({ markOrderForExternalProcessing }) => markOrderForExternalProcessing);

export const updateOrderAddressApi = ({ orderId, mpi, addressInput, isAutoShipNeeded }) =>
  graphqlClient
    .request(updateOrderAddressGql, {
      orderId,
      addressInput: { mpi, ...addressInput },
      isAutoShipNeeded,
    })
    .then(({ updateOrderAddress }) => {
      // eslint-disable-next-line no-underscore-dangle
      if (updateOrderAddress?.__typename === 'Addresses') {
        return updateOrderAddress;
      }

      return transformPatientAddress(updateOrderAddress);
    });

export const prescriptionNeedsReviewAPI = ({ prescriptionUUIDs, patientId, reviewTarget }) =>
  graphqlClient
    .request(prescriptionNeedsReviewGql, { prescriptionUUIDs, patientId, reviewTarget })
    .then(({ prescriptionNeedsReview }) => prescriptionNeedsReview);

export const changePrescriptionReviewReasonApi = ({
  prescriptionUUID,
  patientId,
  lineItemId,
  reviewTarget,
}) =>
  graphqlClient
    .request(changePrescriptionReviewReasonGql, {
      prescriptionUUID,
      patientId,
      lineItemId,
      reviewTarget,
    })
    .then(({ changePrescriptionReviewReason }) => changePrescriptionReviewReason);

export const getOrdersApi = ({ page, sortBy, filter }) => {
  return graphqlClient
    .request(getOrdersGql, {
      offset: (page - 1) * 25,
      sortBy,
      filter: reduce(
        filter,
        (result, value, key) => ({ ...result, [key]: isNull(value) ? undefined : value }),
        {}
      ),
    })
    .then(transformListPomOrders);
};

export const reverifyOrderApi = ({ orderId, mpi }) => {
  return graphqlClient
    .request(reverifyOrderGql, { orderId, mpi })
    .then(({ reverifyOrder }) => reverifyOrder);
};

export const listProcessedExternalOrdersApi = ({ page, sortBy, filter }) => {
  const defaultItemsPerPage = Number(process.env.REACT_APP_DEFAULT_ITEMS_PER_PAGE);

  return graphqlClient
    .request(listProcessedExternalOrdersGql, {
      limit: defaultItemsPerPage,
      offset: (page - 1) * defaultItemsPerPage,
      sortBy,
      filter: reduce(
        filter,
        (result, value, key) => ({ ...result, [key]: isNull(value) ? undefined : value }),
        {}
      ),
    })
    .then(transformProcessedExternalOrders);
};

export const listBatchShipmentsApi = ({ page, sortOrder, filter }) => {
  const defaultItemsPerPage = Number(process.env.REACT_APP_DEFAULT_ITEMS_PER_PAGE);

  return graphqlClient
    .request(listBatchShipmentsGql, {
      limit: defaultItemsPerPage,
      offset: (page - 1) * defaultItemsPerPage,
      filter,
      sortOrder,
    })
    .then(transformListBatchShipments);
};

export const listShipmentLineItemsApi = ({ addressDirectoryId, partnerId, practiceId }) => {
  return graphqlClient
    .request(listShipmentLineItemsGql, {
      limit: 0,
      offset: 0,
      addressDirectoryId,
      partnerId,
      practiceId,
    })
    .then(transformShipmentLineItems);
};

export const getRecipientAddressesApi = ({ orderIds }) =>
  graphqlClient
    .request(getRecipientAddressesGql, { orderIds })
    .then((response) => response.getRecipientAddresses || []);

export const updateAddressOrderLineItemStatusApi = ({ lineItemId, addressDirectoryId, status }) =>
  graphqlClient.request(updateAddressOrderLineItemStatusGql, {
    lineItemIds: [lineItemId],
    addressDirectoryId,
    status,
  });

export const updateOrderAssociationApi = ({
  patientId,
  partnerId,
  practiceId,
  orderId,
  partnerAccountId,
  includeAllFacilities,
}) =>
  graphqlClient.request(updateOrderAssociationGql, {
    patientId,
    partnerId,
    practiceId,
    orderId,
    partnerAccountId,
    includeAllFacilities,
  });

export const createShipmentForLineItemsApi = ({
  lineItemIds,
  addressDirectoryId,
  nameOnAddress,
  shippingOption,
  carrierCode,
  trackingNumber,
  shipmentMode,
  externalShipmentId,
  shipDate,
  shipTime,
  insuranceAmountInUSD,
}) =>
  graphqlClient.request(createShipmentForLineItemsGql, {
    lineItemIds,
    addressDirectoryId,
    nameOnAddress,
    shippingOption,
    carrierCode,
    trackingNumber,
    shipmentMode,
    externalShipmentId,
    shipDate,
    shipTime,
    ...(insuranceAmountInUSD !== undefined && { insuranceAmountInUSD }),
  });

export const listShippingOptionsApi = ({ partnerUUID, practiceId } = {}) =>
  graphqlClient
    .request(listShippingOptionsGql, { partnerUUID, practiceId })
    .then(({ listShippingOptions }) => listShippingOptions);

export const getBatchShipmentsApi = ({ limit, offset, batchShipmentId }) =>
  graphqlClient
    .request(getBatchShipmentsGql, { limit, offset, batchShipmentId })
    .then(transformBatchShipments);

export const createReshipmentApi = ({
  input,
  patientAddressId,
  addressDirectoryId,
  isManuallyVerified,
  isValidationRequired,
  deliveryInstructions,
}) =>
  graphqlClient
    .request(createReshipmentGql, {
      input,
      patientAddressId,
      addressDirectoryId,
      isManuallyVerified,
      isValidationRequired,
      deliveryInstructions,
    })
    .then(({ createReshipment }) => createReshipment);

export const getRxProofUploadUrlsApi = ({ filename, contentType }) =>
  graphqlClient
    .request(getRxProofUploadUrlsGql, { input: { files: [{ filename, contentType }] } })
    .then(({ getRxProofUploadUrls }) => getRxProofUploadUrls);

export const createPomOrderApi = ({ order }) =>
  graphqlClient
    .request(createPomOrderGql, { input: order })
    .then(({ createPomOrder }) => createPomOrder);

export const getUnReconciledShipmentsListApi = ({ page = 1, limit = 25, filter }) => {
  const variables = {
    limit,
    offset: (page - 1) * limit,
    filter,
  };

  return graphqlClient
    .request(listUnReconcileOrdersGql, variables)
    .then(transformUnReconcileShipments);
};

export const deleteIrreconciledOrderApi = ({ spOrderIds }) => {
  return graphqlClient.request(deleteIrreconciledOrderGql, { spOrderIds }).then((response) => {
    return response;
  });
};

export const getOrderSuggestionsForShipstationOrdersApi = ({
  input,
  filter,
  page = 1,
  limit = 25,
}) => {
  const variables = {
    input,
    filter,
    limit,
    offset: (page - 1) * limit,
  };

  return graphqlClient
    .request(getOrderSuggestionsForShipstationOrdersGql, variables)
    .then(transformReconcileOrderSuggestions);
};

export const associateOrderShipmentApi = ({ input }) => {
  return graphqlClient
    .request(associateOrderShipmentGql, { input })
    .then((response) => {
      return response;
    })
    .catch((error) => {
      throw error;
    });
};

export const voidShipmentApi = ({ mpi, lineItemIds, orderId, orderStatus }) => {
  return graphqlClient
    .request(voidShipmentGql, {
      mpi,
      lineItemIds,
      orderId,
      orderStatus,
    })
    .then((response) => {
      return response;
    });
};

export const createPOMOTCOrderApi = ({ input }) => {
  return graphqlClient.request(createPOMOTCOrderGql, { input }).then(({ createPOMOTCOrder }) => {
    return createPOMOTCOrder;
  });
};

export const getOrderShipmentsApi = ({ limit, offset, sortBy, filter }) => {
  return graphqlClient
    .request(listShipmentsFromPrxGql, { limit, offset, sortBy: buildPrxSortBy(sortBy), filter })
    .then(transformPrxShipmentListResponse);
};

export const transferOrderDetailsApi = ({ orderId }) => {
  return graphqlClient.request(transferOrderDetailsGql, { orderId }).then(
    ({ transferOrderDetails }) =>
      transferOrderDetails && {
        ...transferOrderDetails,
        lineItems: (transferOrderDetails.lineItems || []).map((item) => ({
          ...item,
          itemName: item.itemName ? titleCase(item.itemName) : item.itemName,
          overriddenItemName: item.overriddenItemName
            ? titleCase(item.overriddenItemName)
            : item.overriddenItemName,
        })),
      }
  );
};

export const generateLabelForShipmentApi = ({ shipmentId }) => {
  return graphqlClient
    .request(generateLabelForShipmentGql, { shipmentId })
    .then(transformPrxGenerateLabelResponse);
};

export const getShipmentLabelApi = ({ shipmentId }) => {
  return graphqlClient
    .request(getShipmentLabelGql, { shipmentId })
    .then(({ getShipmentLabel }) => getShipmentLabel);
};

export const updateShipmentApi = ({ input, signal }) => {
  const {
    shipmentId,
    referenceId,
    serviceCode,
    packageCode,
    dimensions,
    shipTo,
    orderWeight,
    insuranceAmountInUSD,
    shipDate,
    enableSaturdayDelivery,
    signatureRequired,
    enableInsurance,
    deliveryInstructions,
  } = input;

  const prxInput = {
    ...(serviceCode && { carrierServiceCode: serviceCode }),
    ...(packageCode && { carrierPackageCode: packageCode }),
    ...(dimensions && { packageDimensions: dimensions }),
    ...(orderWeight && { orderWeight }),
    ...(deliveryInstructions !== undefined && {
      deliveryInstructions: deliveryInstructions || undefined,
    }),
    ...(shipTo && {
      shippingAddress: {
        nameOnAddress: shipTo.nameOnAddress,
        phoneNumber: shipTo.phoneNumber,
        address: {
          line1: shipTo.line1,
          line2: shipTo.line2,
          city: shipTo.city,
          state: shipTo.state,
          zip: shipTo.zip,
        },
      },
    }),
    ...(signatureRequired !== undefined && { signatureRequired }),
    ...((insuranceAmountInUSD !== undefined ||
      shipDate ||
      enableSaturdayDelivery !== undefined ||
      enableInsurance !== undefined) && {
      clientMetadata: {
        ...(insuranceAmountInUSD !== undefined && { insuranceAmountInUSD }),
        ...(shipDate && { shipDate }),
        ...(enableSaturdayDelivery !== undefined && { enableSaturdayDelivery }),
        ...(enableInsurance !== undefined && { enableInsurance }),
      },
    }),
  };

  return graphqlClient
    .request({
      document: updateShipmentGql,
      variables: { shipmentId, referenceId, input: prxInput },
      signal,
    })
    .then(({ updateShipment }) => transformPrxUpdateShipmentResponse(updateShipment));
};

export const cancelPrxShipmentApi = ({ trackingNumber }) => {
  return graphqlClient
    .request(cancelPrxShipmentGql, { trackingNumber })
    .then(({ cancelShipment }) => cancelShipment);
};

export const deletePrxShipmentApi = ({ shipmentId, reason, cancelType }) => {
  return graphqlClient
    .request(deletePrxShipmentGql, { shipmentId, reason, cancelType })
    .then(({ deleteShipmentRequest }) => deleteShipmentRequest);
};

export const cancelOrderShipmentApi = ({ shipmentId }) => {
  return graphqlClient
    .request(cancelOrderShipmentGql, { shipmentId })
    .then(({ cancelOrderShipment }) => cancelOrderShipment);
};

export const validateShipmentAddressApi = ({ input }) => {
  return graphqlClient
    .request(validateShipmentAddressGql, { input })
    .then(({ validateAddress }) => validateAddress);
};

export const getShipmentCountsApi = ({ filter, groupBy }) => {
  return graphqlClient
    .request(getShipmentCountsGql, { filter, groupBy })
    .then(({ countShipments }) => countShipments);
};

export const getShipmentCountsBatchApi = ({ queries }) => {
  return graphqlClient
    .request(getShipmentCountsBatchGql, { queries })
    .then(({ countShipmentsBatch }) => countShipmentsBatch);
};

export const getShipstationShipmentsApi = ({ limit, offset, filter, sortBy }) => {
  return graphqlClient
    .request(getShipstationShipmentsGql, { limit, offset, filter, sortBy })
    .then(({ getShipstationShipments }) => ({
      ...getShipstationShipments,
      result: getShipstationShipments.result.map((shipment) => ({
        ...shipment,
        lineItems: shipment.lineItems.map((lineItem) => ({
          ...lineItem,
          drugName: lineItem.drugName ? titleCase(lineItem.drugName) : lineItem.drugName,
        })),
      })),
    }));
};

export const getShipstationShipmentCountsBatchApi = ({ queries }) => {
  return graphqlClient
    .request(getShipstationShipmentCountsBatchGql, { queries })
    .then(({ getShipstationShipmentCountsBatch }) => getShipstationShipmentCountsBatch);
};

export const getShipmentEventHistoryApi = ({ orderId }) => {
  return graphqlClient
    .request(getShipmentEventHistoryGql, { orderId })
    .then(({ getShipmentEventHistory }) => getShipmentEventHistory);
};

export const sendOrderBackToQueueApi = ({ orderIds, rollbackType }) => {
  return graphqlClient
    .request(sendOrderBackToQueueGql, { input: { orderIds, rollbackType } })
    .then(({ sendOrderBackToQueue }) => sendOrderBackToQueue);
};

export const shipOrderLaterApi = ({ orderId, shipByDate }) => {
  return graphqlClient
    .request(shipOrderLaterGql, { input: { orderId, shipByDate } })
    .then(({ shipOrderLater }) => shipOrderLater);
};

export const applyUrgentTagApi = ({ lineItemId, shipByDate, reason }) => {
  return graphqlClient
    .request(applyUrgentTagGql, {
      input: {
        lineItemId,
        ...(shipByDate && { shipByDate }),
        ...(reason && { reason }),
      },
    })
    .then(({ applyUrgentTag }) => applyUrgentTag);
};

export const removeUrgentTagApi = ({ lineItemId, shipByDate, reason }) => {
  return graphqlClient
    .request(removeUrgentTagGql, {
      input: {
        lineItemId,
        ...(shipByDate && { shipByDate }),
        ...(reason && { reason }),
      },
    })
    .then(({ removeUrgentTag }) => removeUrgentTag);
};

export const setLineItemShipByDateApi = ({ lineItemId, shipByDate, reason }) => {
  return graphqlClient
    .request(setLineItemShipByDateGql, { input: { lineItemId, shipByDate, reason } })
    .then(({ setLineItemShipByDate }) => setLineItemShipByDate);
};

export const clearShipByDateOverrideApi = ({ lineItemId }) => {
  return graphqlClient
    .request(clearShipByDateOverrideGql, { lineItemId })
    .then(({ clearShipByDateOverride }) => clearShipByDateOverride);
};
